import React, {useContext, useState} from "react";
import {toast} from "react-toastify";
import {
    Box, Button, IconButton, InputBase, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Tooltip, Typography
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Add01Icon, Delete02Icon, PencilEdit01Icon, Search01Icon} from "@hugeicons/core-free-icons";
import {SignInContext} from "../../context/SignInContext";
import {useCatalog} from "../../context/CatalogContext";
import useConfirm from "../../hooks/useConfirm";
import {VoiceModelRepository} from "../../repositories/VoiceModelRepository";
import {VoiceModelDialog} from "./components/voice-model-dialog";
import {controlSx, Panel} from "../registry-project/dashboard/components/Panel";

const getErrorMessage = (e, fallback) => e?.response?.data?.message || fallback;

const headCellSx = {
    height: 40, py: 0.5, px: 1.5, fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: "#8A8AA0",
    bgcolor: "#FAFAFD", borderBottom: "1px solid #ECECF3", whiteSpace: "nowrap",
};
const cellSx = {py: 1.25, px: 1.5, fontSize: 13, color: "#23264D", borderBottom: "1px solid #F2F2F7"};
const iconButtonSx = {borderRadius: "50% !important", color: "#8A8AA0", "&:hover": {bgcolor: "#F4F4FA", color: "#4A4A63"}};
const deleteIconButtonSx = {...iconButtonSx, "&:hover": {bgcolor: "rgba(220, 38, 38, 0.08)", color: "#DC2626"}};
const headerButtonSx = {
    height: 38, px: 1.75, borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 700,
    color: "#374151", bgcolor: "#FAFAFD", "&:hover": {bgcolor: "#ECECF3"},
};

const AccessPill = ({isPublic, label}) => (
    <Box component={"span"} sx={{
        px: 1, py: 0.25, borderRadius: "999px", fontSize: 11, fontWeight: 600,
        color: isPublic ? "#6B6B80" : "#4B55C9",
        bgcolor: isPublic ? "#F2F2F7" : "rgba(132, 145, 247, 0.12)",
    }}>
        {label}
    </Box>
);

// Admin catalog of text-to-speech models available to the platform's projects.
export const RegistryVoiceModelsPage = () => {
    const {currentUser, organizations} = useContext(SignInContext);
    const canManage = currentUser?.roles?.includes("admin");

    const {items: models, loading, setItems: setModels} = useCatalog(canManage ? "voiceModels" : "myVoiceModels", {refreshOnMount: true});
    const [openModal, setOpenModal] = useState(false);
    const [selectedModel, setSelectedModel] = useState(null);
    const [search, setSearch] = useState("");

    const confirmDelete = useConfirm("Are you sure you want to delete this voice model?");

    const getOrgName = (orgId) => {
        if (!orgId) return "Public";
        return organizations?.find(o => o.id === orgId)?.name || orgId;
    };

    const query = search.trim().toLowerCase();
    const filteredModels = query
        ? models.filter(model => [model.name, model.provider, model.modelId, getOrgName(model.organizationId)]
            .some(value => String(value || "").toLowerCase().includes(query)))
        : models;

    const handleOpenModal = (model = null) => {
        setSelectedModel(model);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedModel(null);
    };

    const handleSave = async (model) => {
        const {apiKey, connectionString, ...data} = model;
        try {
            if (selectedModel?.id) {
                const response = await VoiceModelRepository.edit(selectedModel.id, data);
                setModels(prev => prev.map(m => m.id === selectedModel.id ? response.data : m));
            } else {
                const response = await VoiceModelRepository.create({...data, apiKey, connectionString});
                setModels(prev => [...prev, response.data]);
            }
            toast.success("Voice model saved successfully");
            return true;
        } catch (e) {
            console.error(e);
            toast.error(getErrorMessage(e, "Failed to save voice model"));
            return false;
        }
    };

    const handleDelete = async (model) => {
        const confirmed = await confirmDelete({title: `Delete "${model.name || model.modelId}"?`});
        if (!confirmed) return;
        try {
            await VoiceModelRepository.delete(model.id);
            setModels(prev => prev.filter(m => m.id !== model.id));
            toast.success("Voice model deleted successfully");
        } catch (e) {
            console.error(e);
            toast.error(getErrorMessage(e, "Failed to delete voice model"));
        }
    };

    const emptyMessage = models.length === 0 ? "No voice models added yet" : `No models match "${search}"`;
    const columnCount = canManage ? 5 : 4;

    return (
        <Stack gap={3} sx={{p: {xs: 2, md: 3}, maxWidth: 1200, width: "100%", mx: "auto", boxSizing: "border-box"}}>
            <VoiceModelDialog
                open={openModal}
                selectedModel={selectedModel}
                handleClose={handleCloseModal}
                organizations={organizations}
                onSubmit={handleSave}
            />

            <Stack direction={{xs: "column", md: "row"}} justifyContent={"space-between"} alignItems={{md: "flex-end"}} gap={2}>
                <Box>
                    <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7"}}>MODELS</Typography>
                    <Typography component={"h1"} sx={{fontSize: 26, fontWeight: 700, color: "#23264D", letterSpacing: "-0.02em"}}>
                        Synthesize Models
                    </Typography>
                    <Typography sx={{fontSize: 14, color: "#6B6B80"}}>
                        Manage text-to-speech models across the platform
                    </Typography>
                </Box>
                <Stack direction={"row"} alignItems={"center"} gap={1.25} flexWrap={"wrap"}>
                    <Box component={"label"} sx={{...controlSx, width: 220}}>
                        <HugeiconsIcon icon={Search01Icon} size={16}/>
                        <InputBase
                            placeholder={"Search models..."}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            inputProps={{"aria-label": "Search models"}}
                            sx={{flex: 1, fontSize: 13, color: "#23264D"}}
                        />
                    </Box>
                    {canManage && (
                        <Button startIcon={<HugeiconsIcon icon={Add01Icon} size={17}/>} onClick={() => handleOpenModal()} sx={headerButtonSx}>
                            Add Model
                        </Button>
                    )}
                </Stack>
            </Stack>

            <Panel sx={{overflow: "hidden"}}>
                <TableContainer sx={{overflowX: "auto"}}>
                    <Table size={"small"} sx={{minWidth: 720}}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{...headCellSx, pl: 2.5}}>NAME</TableCell>
                                <TableCell sx={headCellSx}>PROVIDER</TableCell>
                                <TableCell sx={headCellSx}>MODEL ID</TableCell>
                                <TableCell sx={{...headCellSx, pr: canManage ? 1.5 : 2.5}}>ACCESS</TableCell>
                                {canManage && <TableCell sx={{...headCellSx, pr: 2.5}}/>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && models.length === 0 && Array.from({length: 5}).map((_, index) => (
                                <TableRow key={index}>
                                    <TableCell colSpan={columnCount} sx={{...cellSx, px: 2.5}}><Skeleton height={32}/></TableCell>
                                </TableRow>
                            ))}
                            {!loading && filteredModels.map(model => (
                                <TableRow key={model.id} hover sx={{"&.MuiTableRow-hover:hover": {bgcolor: "#FAFAFD"}}}>
                                    <TableCell sx={{...cellSx, pl: 2.5, fontWeight: 600}}>
                                        {model.name || "—"}
                                        {model.baseUrl && (
                                            <Typography noWrap sx={{fontSize: 12, fontWeight: 400, color: "#8A8AA0", maxWidth: 220}}>{model.baseUrl}</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell sx={cellSx}>{model.provider}</TableCell>
                                    <TableCell sx={{...cellSx, fontFamily: "monospace", fontSize: 12.5}}>{model.modelId || "—"}</TableCell>
                                    <TableCell sx={cellSx}>
                                        <AccessPill isPublic={!model.organizationId} label={getOrgName(model.organizationId)}/>
                                    </TableCell>
                                    {canManage && (
                                        <TableCell sx={{...cellSx, pr: 2.5, whiteSpace: "nowrap"}}>
                                            <Stack direction={"row"} gap={0.25} justifyContent={"flex-end"}>
                                                <Tooltip title={"Edit"}>
                                                    <IconButton size={"small"} sx={iconButtonSx} onClick={() => handleOpenModal(model)}>
                                                        <HugeiconsIcon icon={PencilEdit01Icon} size={16}/>
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={"Delete"}>
                                                    <IconButton size={"small"} sx={deleteIconButtonSx} onClick={() => handleDelete(model)}>
                                                        <HugeiconsIcon icon={Delete02Icon} size={16}/>
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                            {!loading && filteredModels.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={columnCount} sx={{...cellSx, borderBottom: 0, py: 6, textAlign: "center", color: "#8A8AA0"}}>
                                        {emptyMessage}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Panel>
        </Stack>
    );
};
